import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { find } from "lodash/fp";

import type { Webhook } from "@app/analytics/types/webhookTypes";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import { replaceWhere } from "@app/utils/arrays";

const EMPTY_WEBHOOKS: Webhook[] = [];

const useWebhooks = ({ enabled }: { enabled: boolean } = { enabled: true }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["integrations", "webhooks", user?.id];

  const webhooksQuery = useQuery({
    queryKey,
    queryFn: apiClient.integrations.webhooks.listWebhooks,
    enabled,
  });

  const createWebhookMutation = useMutation({
    mutationFn: apiClient.integrations.webhooks.createWebhook,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const updateWebhookMutation = useMutation({
    mutationFn: apiClient.integrations.webhooks.updateWebhook,
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      const webhooks: Webhook[] =
        queryClient.getQueryData(queryKey) ?? EMPTY_WEBHOOKS;
      const webhookToUpdate = find({ id: body.id }, webhooks);

      if (webhookToUpdate) {
        const updatedWebhook = { ...webhookToUpdate, ...body };
        const updatedData = replaceWhere(
          webhooks,
          updatedWebhook,
          ({ id }) => id === body.id
        );
        queryClient.setQueryData(queryKey, updatedData);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: apiClient.integrations.webhooks.deleteWebhook,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const sendTestRequestMutation = useMutation({
    mutationFn: apiClient.integrations.webhooks.sendTestRequest,
    onSuccess: (res) => {
      if (res.statusCode === 200) {
        toast.success("Your test API call was successful!");
      } else {
        toast.error(
          `Your test API call resulted in an error (${res.statusCode})`
        );
      }
    },
    onError: () => {
      toast.error(
        "Something went wrong and the test request could not be sent. Please try again later or contact support@read.ai if the problem persists."
      );
    },
  });

  return {
    isError: webhooksQuery.isError,
    isLoading: webhooksQuery.isLoading,
    webhooks: webhooksQuery.data ?? EMPTY_WEBHOOKS,

    createWebhook: createWebhookMutation,
    updateWebhook: updateWebhookMutation,
    deleteWebhook: deleteWebhookMutation,
    sendTestRequest: sendTestRequestMutation,
  };
};

export default useWebhooks;
